import { useState } from 'react';
import { MapPin, Phone, Package, ChevronRight, CheckCircle, Clock, Truck } from 'lucide-react';
import DriverExpressDetail from './DriverExpressDetail';

const STATUS_CONFIG = {
  pending:    { label: 'Pendiente',  color: 'bg-yellow-100 text-yellow-800', Icon: Clock },
  en_recojo: { label: 'En recojo', color: 'bg-orange-100 text-orange-800', Icon: Truck },
  encamino:   { label: 'En camino',  color: 'bg-blue-100 text-blue-800',    Icon: Truck },
  entregado:  { label: 'Entregado',  color: 'bg-green-100 text-green-800',  Icon: CheckCircle },
  rechazado:  { label: 'Rechazado',  color: 'bg-red-100 text-red-800',      Icon: Clock },
  cancelado:  { label: 'Cancelado',  color: 'bg-gray-100 text-gray-500',    Icon: Clock },
  postergado: { label: 'Postergado', color: 'bg-purple-100 text-purple-800',Icon: Clock },
};

function ActiveDeliveryCard({ delivery, onOpenDetail, onOpenMap }) {
  const cfg = STATUS_CONFIG[delivery.status] || STATUS_CONFIG.pending;
  const StatusIcon = cfg.Icon;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
      {/* Status bar */}
      <div className={`px-4 py-2 flex items-center gap-2 ${cfg.color}`}>
        <StatusIcon size={14} />
        <span className="text-xs font-semibold">{cfg.label}</span>
        <span className="ml-auto text-xs font-mono">#{delivery.id}</span>
      </div>

      <div className="p-4 space-y-4">
        {/* Earnings */}
        <div className="bg-brand-green/10 rounded-xl px-4 py-3 text-center">
          <div className="text-xs text-gray-500 mb-0.5">Tu ganancia</div>
          <div className="text-2xl font-bold text-brand-green">
            S/ {Number(delivery.driver_earnings).toFixed(2)}
          </div>
          <div className="text-xs text-gray-400">{delivery.distance_km} km</div>
        </div>

        {/* Route */}
        <div className="space-y-2">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 w-7 h-7 rounded-full bg-brand-green/15 flex items-center justify-center shrink-0">
              <MapPin size={13} className="text-brand-green" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-gray-400 font-medium">Recojo</div>
              <div className="text-sm text-gray-800 leading-snug">{delivery.pickup_address}</div>
            </div>
          </div>
          <div className="ml-3.5 w-px h-4 bg-gray-200" />
          <div className="flex items-start gap-3">
            <div className="mt-0.5 w-7 h-7 rounded-full bg-brand-primary/15 flex items-center justify-center shrink-0">
              <MapPin size={13} className="text-brand-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-gray-400 font-medium">Entrega</div>
              <div className="text-sm text-gray-800 leading-snug">{delivery.dropoff_address}</div>
            </div>
          </div>
        </div>

        {/* Recipient */}
        <div className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2.5">
          <div>
            <div className="text-xs text-gray-400">Destinatario</div>
            <div className="text-sm font-medium text-gray-800">{delivery.recipient_name}</div>
          </div>
          <a
            href={`tel:${delivery.recipient_phone}`}
            onClick={e => e.stopPropagation()}
            className="w-9 h-9 bg-brand-green rounded-full flex items-center justify-center shrink-0"
          >
            <Phone size={15} className="text-white" />
          </a>
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => onOpenMap(delivery)}
            className="flex items-center justify-center gap-2 bg-brand-primary text-white py-3 rounded-xl text-sm font-semibold active:scale-95 transition-transform"
          >
            <MapPin size={15} />
            Ver ruta
          </button>
          <button
            onClick={() => onOpenDetail(delivery)}
            className="flex items-center justify-center gap-2 bg-gray-100 text-gray-700 py-3 rounded-xl text-sm font-semibold active:scale-95 transition-transform"
          >
            <Package size={15} />
            Detalle
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DriverExpressHome({
  user_id,
  deliveries,
  activeDeliveries,
  loading,
  updateDelivery,
  onOpenMap,       // ← comes from DriverExpressDashboard, renders map outside overflow
}) {
  const [detailDelivery, setDetailDelivery] = useState(null);

  const completedToday = deliveries.filter(d => d.status === 'entregado');
  const earnedToday    = completedToday.reduce((s, d) => s + Number(d.driver_earnings), 0);

  if (loading) {
    return (
      <div className="p-4 space-y-3">
        {[1, 2].map(i => (
          <div key={i} className="bg-white rounded-2xl h-48 animate-pulse border border-gray-100" />
        ))}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">

      {/* Today's summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-3 text-center">
          <div className="text-xs text-gray-400 mb-1">Completadas hoy</div>
          <div className="text-2xl font-bold text-gray-800">{completedToday.length}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-3 text-center">
          <div className="text-xs text-gray-400 mb-1">Ganado hoy</div>
          <div className="text-2xl font-bold text-brand-green">
            S/ {earnedToday.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Active deliveries */}
      {activeDeliveries.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
          <CheckCircle size={40} className="text-gray-200 mx-auto mb-3" />
          <div className="text-gray-500 font-medium">Sin entregas activas</div>
          <div className="text-xs text-gray-400 mt-1">
            El administrador te asignará nuevas entregas
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-1">
            Entregas activas ({activeDeliveries.length})
          </div>
          {activeDeliveries.map(d => (
            <ActiveDeliveryCard
              key={d.id}
              delivery={d}
              onOpenDetail={setDetailDelivery}
              onOpenMap={(d) => onOpenMap({ delivery: d })}
            />
          ))}
        </div>
      )}

      {/* Completed today preview */}
      {completedToday.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-1">
            Completadas hoy
          </div>
          {completedToday.map(d => (
            <button
              key={d.id}
              onClick={() => setDetailDelivery(d)}
              className="w-full bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-3 active:bg-gray-50 transition-colors"
            >
              <CheckCircle size={16} className="text-brand-green shrink-0" />
              <div className="flex-1 min-w-0 text-left">
                <div className="text-sm font-medium text-gray-800 truncate">{d.recipient_name}</div>
                <div className="text-xs text-gray-400 truncate">{d.dropoff_address}</div>
              </div>
              <div className="text-sm font-bold text-brand-green shrink-0">
                S/ {Number(d.driver_earnings).toFixed(2)}
              </div>
              <ChevronRight size={14} className="text-gray-300 shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* Detail modal — still local, it's a bottom sheet not a fixed fullscreen */}
      {detailDelivery && (
        <DriverExpressDetail
          delivery={detailDelivery}
          user_id={user_id}
          onClose={() => setDetailDelivery(null)}
          onUpdate={(updated) => {
            updateDelivery(updated);
            setDetailDelivery(prev => ({ ...prev, ...updated }));
          }}
          onOpenMap={({ delivery: d, focusOnPickup = false } = {}) => {
            onOpenMap({ delivery: d || detailDelivery, focusOnPickup });
            setDetailDelivery(null);
          }}
        />
      )}
    </div>
  );
}